import type { MarginInventoryHistoryResponse, MarginTerm } from "@yutai/shared"
import useSWR from "swr"
import styles from "./MarginInventoryHistory.module.css"
import { type HistoryColumn, buildHistoryColumns, groupByYear } from "./inventoryHistory.js"
import { adminFetch } from "./marginInventoryApi.js"

const TERM_LABEL: Record<MarginTerm, string> = { "14d": "14日", unlimited: "無期限" }
const TERM_COLOR: Record<MarginTerm, string> = { "14d": "#2563eb", unlimited: "#d97706" }
const TERMS: MarginTerm[] = ["14d", "unlimited"]

const W = 640
const H = 220
const PAD = { top: 16, right: 12, bottom: 28, left: 48 }

const dayMs = (date: string) => Date.parse(`${date}T00:00:00Z`)
const fmt = (n: number) => n.toLocaleString("ja-JP")

function niceMax(v: number): number {
  if (v <= 0) return 1
  const mag = 10 ** Math.floor(Math.log10(v))
  const n = v / mag
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * mag
}

// x=日付, y=在庫数。年が変わる位置に縦線と年ラベルを入れる。
function Chart({ columns }: { columns: HistoryColumn[] }) {
  const chrono = [...columns].reverse()
  if (chrono.length === 0) return null
  const t0 = dayMs(chrono[0].date)
  const t1 = dayMs(chrono[chrono.length - 1].date)
  const span = Math.max(t1 - t0, 1)
  const maxQ = niceMax(
    Math.max(0, ...columns.flatMap((c) => TERMS.map((t) => c.quantities[t] ?? 0)))
  )
  const x = (date: string) =>
    chrono.length === 1
      ? (PAD.left + W - PAD.right) / 2
      : PAD.left + ((dayMs(date) - t0) / span) * (W - PAD.left - PAD.right)
  const y = (q: number) => PAD.top + (1 - q / maxQ) * (H - PAD.top - PAD.bottom)

  // 年の境目（各年の1/1）
  const yearLines: { year: number; px: number }[] = []
  for (
    let yr = Number(chrono[0].date.slice(0, 4)) + 1;
    yr <= Number(chrono[chrono.length - 1].date.slice(0, 4));
    yr++
  ) {
    yearLines.push({ year: yr, px: x(`${yr}-01-01`) })
  }
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((r) => r * maxQ)

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={styles.chart} role="img" aria-label="在庫数の推移">
      {yTicks.map((q) => (
        <g key={q}>
          <line x1={PAD.left} x2={W - PAD.right} y1={y(q)} y2={y(q)} className={styles.grid} />
          <text x={PAD.left - 6} y={y(q) + 4} textAnchor="end" className={styles.axisText}>
            {fmt(q)}
          </text>
        </g>
      ))}
      {yearLines.map((l) => (
        <g key={l.year}>
          <line x1={l.px} x2={l.px} y1={PAD.top} y2={H - PAD.bottom} className={styles.yearLine} />
          <text x={l.px + 3} y={PAD.top + 10} className={styles.yearText}>
            {l.year}
          </text>
        </g>
      ))}
      <text x={PAD.left} y={H - 8} textAnchor="start" className={styles.axisText}>
        {chrono[0].date.replaceAll("-", "/")}
      </text>
      {chrono.length > 1 && (
        <text x={W - PAD.right} y={H - 8} textAnchor="end" className={styles.axisText}>
          {chrono[chrono.length - 1].date.replaceAll("-", "/")}
        </text>
      )}
      {TERMS.map((term) => {
        const pts = chrono
          .filter((c) => c.quantities[term] !== null)
          .map((c) => ({ px: x(c.date), py: y(c.quantities[term] ?? 0) }))
        return (
          <g key={term} stroke={TERM_COLOR[term]} fill={TERM_COLOR[term]}>
            <polyline
              points={pts.map((p) => `${p.px},${p.py}`).join(" ")}
              fill="none"
              strokeWidth={1.5}
            />
            {pts.length <= 60 &&
              pts.map((p) => <circle key={p.px} cx={p.px} cy={p.py} r={2} stroke="none" />)}
          </g>
        )
      })}
    </svg>
  )
}

// 在庫数をクリックすると開く、1銘柄の全期間履歴（表は左が最新）とグラフ。
export function MarginInventoryHistory({ code }: { code: string }) {
  const { data, error } = useSWR<MarginInventoryHistoryResponse>(
    ["admin-margin-inventory-history", code],
    () => adminFetch(`/margin-inventory/history/${encodeURIComponent(code)}`),
    { revalidateOnFocus: false }
  )

  if (error) return <p className={styles.error}>履歴の読み込みに失敗しました</p>
  if (!data) return <p className={styles.notice}>履歴を読み込み中...</p>
  const columns = buildHistoryColumns(data)
  if (columns.length === 0) return <p className={styles.notice}>履歴がありません</p>

  return (
    <div className={styles.wrap}>
      <div className={styles.legend}>
        {TERMS.map((t) => (
          <span key={t} className={styles.legendItem}>
            <span className={styles.swatch} style={{ background: TERM_COLOR[t] }} />
            {TERM_LABEL[t]}
          </span>
        ))}
      </div>
      <Chart columns={columns} />
      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th rowSpan={2} className={styles.corner} />
              {groupByYear(columns).map((g) => (
                <th key={g.year} colSpan={g.count} className={styles.yearCell}>
                  {g.year}年
                </th>
              ))}
            </tr>
            <tr>
              {columns.map((c) => (
                <th key={c.date} className={styles.dateCell}>
                  {Number(c.date.slice(5, 7))}/{Number(c.date.slice(8, 10))}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {TERMS.map((term) => (
              <tr key={term}>
                <th className={styles.rowHead}>{TERM_LABEL[term]}</th>
                {columns.map((c) => (
                  <td key={c.date}>
                    {c.quantities[term] === null ? "—" : fmt(c.quantities[term] ?? 0)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
